// File: src/pages/BookInfo.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import '../styles/BookInfo.css';
import Sidebar from "../components/Sidebar.js";
import Header from "../components/Header.js";
import bigLeftArrow from '../images/bigLeftArrow.png';
import bigRightArrow from '../images/bigRightArrow.png';
import playIcon from '../images/playIcon.png';
import tickReadLaterIcon from '../images/tick_readlater.png';
import readlaterIcon from '../images/readlater_icon.png';
import { insertReadLater, deleteReadLater, fetchPageDetails } from "../services/AllServices.js";

import { speakText } from "../components/utils/speechUtils.js";

const BookInfo = () => {
  const [activeItem, setActiveItem] = useState("dashboard");
  const [searchQuery, setSearchQuery] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const { bookId } = useParams(); // Get bookId from URL
  const [isBookmarked, setIsBookmarked] = useState(false); // State to track bookmark status
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize books to avoid unnecessary re-renders
  const books = useMemo(() => location.state?.books || [], [location.state]);

  useEffect(() => {
    if (books.length > 0) {
      const selectedIndex = books.findIndex((book) => book._id === bookId);
      setCurrentIndex(selectedIndex !== -1 ? selectedIndex : 0);
  
      // Check bookmark status from local storage
      const currentBook = books[selectedIndex];
      const storedBookmarks = JSON.parse(localStorage.getItem("bookmarks")) || {};
      setIsBookmarked(!!storedBookmarks[currentBook?._id]); // Check if the book is bookmarked
    }
  }, [bookId, books]);

  const currentBook = books[currentIndex] || {}; 

  const handlePlayClick = async (event, bookID) => {
    event?.preventDefault?.(); // Prevent default if passed an event
    if (!bookID) {
      console.log('No bookID provided:', bookID);
      return;
    }
  
    try {
      const response = await fetchPageDetails(bookID);
      if (response) {
        navigate("/audiobook-player", { state: { bookData: response } });
      } else {
        console.error("Invalid response structure:", response);
      }
    } catch (error) {
      console.error("Error in handlePlayClick:", error);
    }
  };

  const handleNext = () => {
    if (!books.length) return;
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % books.length;
      const nextBook = books[nextIndex];
      navigate(`/book-info/${nextBook._id}`, { state: { books } });
      return nextIndex;
    });
  };

  const handlePrev = () => {
    if (!books.length) return;
    setCurrentIndex((prevIndex) => {
      const prevIndexCalc = (prevIndex - 1 + books.length) % books.length;
      const prevBook = books[prevIndexCalc];
      navigate(`/book-info/${prevBook._id}`, { state: { books } });
      return prevIndexCalc;
    });
  };

  const handleProfileClick = () => {
    navigate("/settings", { state: { selectedSection: "account" } });
  };

  const resetSearch = () => {
    setSearchQuery("");
  };

  const toggleBookmark = async (bookID) => {
    // Get existing bookmarks from local storage
    const storedBookmarks = JSON.parse(localStorage.getItem("bookmarks")) || {};
  
    if (isBookmarked) {
      // Remove the bookmark
      try {
        await deleteReadLater({ bookID });
      } catch (err) {
        console.error("Error deleting read later:", err);
      }
      delete storedBookmarks[bookID];
      setIsBookmarked(false);
    } else {
      // Add the bookmark
      try {
        await insertReadLater({ bookID });
      } catch (err) {
        console.error("Error inserting read later:", err);
      }
      storedBookmarks[bookID] = true;
      setIsBookmarked(true);
    }
  
    // Save updated bookmarks back to local storage
    localStorage.setItem("bookmarks", JSON.stringify(storedBookmarks));
  };

  const handleActiveItemChange = (item) => {
    setActiveItem(item); // Update active item
    if (item === "dashboard") {
      navigate("/dashboard"); // Navigate to dashboard
    } else if (item === "bookmarks") {
      navigate("/bookmarks"); // Navigate to bookmarks
    } else if (item === "library") {
      navigate("/library"); // Navigate to library
    } else if (item === "settings") {
      navigate("/settings"); // Navigate to settings
    } else if (item === "help") {
      navigate("/help"); // Navigate to help
    }
    
    resetSearch(); // Reset search whenever a new section is selected
  };

  const getHeaderVisibility = () => {
    if (activeItem === "dashboard" || activeItem === "") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "Dashboard" };
    } else if (activeItem === "bookmarks") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "" };
    } else if (activeItem === "library") {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName: "My Library" };
    } else if (activeItem === "settings") {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName: "Account" };
    } else if (activeItem === "help") {
      return { showSearch: true, showUserProfile: true, showArrows: true, pageName: "" };
    } else {
      return { showSearch: false, showUserProfile: true, showArrows: true, pageName:"Search Results"};
    }
  };

  const { showSearch, showUserProfile, showArrows, pageName } = getHeaderVisibility();

  return (
    <main className="main-content">
      <div className="sidebar_container">
        <Sidebar
          activeItem={activeItem}
          setActiveItem={handleActiveItemChange}
          resetSearch={resetSearch}
        />
      </div>

      <div className="bookInfo_container">
         <Header
            showSearch={showSearch}
            showUserProfile={showUserProfile}
            showArrows={showArrows}
            pageName={pageName}
            searchQuery={searchQuery}
            onSearch={(query) => setSearchQuery(query)}
            onProfileClick={handleProfileClick} 
          />

        <div className="bookInfo_body">
          <div className="carousel-container">
            {/* Prev arrow as semantic button */}
            <button
              className="carousel-arrow-button"
              onClick={handlePrev}
              aria-label="Previous book"
              type="button"
              onFocus={() => speakText("Previous book")}
            >
              <div className="left-arrow" aria-hidden="true">
                <img src={bigLeftArrow} alt="left arrow" />
              </div>
            </button>

            <div className="carousel-content">
              <img
                className="bookinfo_cover"
                src={currentBook.thumbnail}
                alt={currentBook.title ? `${currentBook.title} cover` : "Book Cover"}
              />
              <div className="book_info">
                <div className="bookinfo_title_play">
                  <div className="bookinfo_title">{currentBook.title}</div>

                  {/* Play button (SPA action) - semantic button */}
                  <button
                    className="bookinfo_play"
                    type="button"
                    onClick={(e) => handlePlayClick(e, currentBook._id)}
                    onFocus={() => speakText(`Play ${currentBook.title}`)}
                    aria-label={`Play ${currentBook.title}`}
                  >
                    <img src={playIcon} alt="play icon" />
                  </button>
                </div>

                <div className="bookinfo_author_save">
                  <div className="bookinfo_author">{currentBook.author_list?.join(", ")}</div>

                  {/* Bookmark toggle as a button */}
                  <button
                    className="bookinfo_save"
                    type="button"
                    onClick={() => toggleBookmark(currentBook._id)}
                    aria-pressed={isBookmarked}
                    aria-label={isBookmarked ? `Remove ${currentBook.title} from Read Later` : `Add ${currentBook.title} to Read Later`}
                    onFocus={() =>
                      speakText(isBookmarked ? "Bookmarked. Press to remove from Read Later." : "Not bookmarked. Press to add to Read Later.")
                    }
                  >
                    <img
                      src={isBookmarked ? tickReadLaterIcon : readlaterIcon}
                      alt={isBookmarked ? "bookmarked icon" : "add to read later icon"}
                    />
                    <span className="save-text">Add to Read Later</span>
                  </button>
                </div>

                <p className="bookinfo_description">{currentBook.description}</p>
              </div>
            </div>

            {/* Next arrow as semantic button */}
            <button
              className="carousel-arrow-button"
              onClick={handleNext}
              aria-label="Next book"
              type="button"
              onFocus={() => speakText("Next book")}
            >
              <div className="right-arrow" aria-hidden="true">
                <img src={bigRightArrow} alt="right arrow" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BookInfo;
